/*
 * Description:
 * ---------------------
 * The devices the Firewall screen names: those the box sees on its network
 * (/diagnostics/devices) and those a device profile names but the box has not
 * seen lately, each with its name, address, hardware address and the group it
 * is in, and a picture guessed from its name. Read once by the panel and
 * handed to every section, so a device reads the same everywhere.
 *
 * ! file components/FirewallDevices.js
 */
import { useState } from "react";
import {
  TbAccessPoint, TbCamera, TbCashRegister, TbDeviceDesktop, TbDeviceLaptop, TbDeviceMobile, TbDeviceTv, TbPrinter,
} from "react-icons/tb";
import useVisibleInterval from "../hooks/useVisibleInterval";
import { nodeApiUrlFor } from "../serverApi";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import * as DW from "./deviceWords";

/* A picture for a device, guessed from its name; a guess only decorates. */
const KIND_ICONS = [
  [/till|pos|kasa|register|terminal|betting/i, TbCashRegister],
  [/cam|nvr|dvr|hikvision|dahua|axis/i, TbCamera],
  [/phone|iphone|android|pixel|galaxy|redmi/i, TbDeviceMobile],
  [/laptop|macbook|notebook|thinkpad/i, TbDeviceLaptop],
  [/tv|chromecast|roku|firestick|screen|display/i, TbDeviceTv],
  [/print|epson|brother|canon|zebra/i, TbPrinter],
  [/eap|ap-|access.?point|switch|router|unifi|omada/i, TbAccessPoint],
];
export const kindIcon = (name) => (KIND_ICONS.find(([re]) => re.test(name)) || [null, TbDeviceDesktop])[1];

/* The devices the box sees on its network, read again every 15 seconds. */
export function useLanDevices(selectedNode) {
  const [lan, setLan] = useState([]);
  useVisibleInterval(async () => {
    const res = await fetchWithTimeout(nodeApiUrlFor(selectedNode)("/diagnostics/devices")).catch(() => null);
    if (res?.ok) setLan((await res.json().catch(() => null))?.devices || []);
  }, 15000, !!selectedNode);
  return lan;
}

/* One row per device: those the box sees, and those assigned but not seen
   lately, each with the group it is in. */
export function deviceRows(lan, assigned) {
  const rows = new Map();
  for (const d of lan) {
    const mac = DW.normalizeMac(d.mac);
    if (mac) rows.set(mac, { mac, name: d.hostname && d.hostname !== "*" ? d.hostname : d.ip || mac, ip: d.ip || "" });
  }
  for (const mac of Object.keys(assigned)) if (!rows.has(mac)) rows.set(mac, { mac, name: mac, ip: "" });
  return [...rows.values()]
    .map((d) => ({ ...d, profile: assigned[d.mac]?.profile || "" }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/* A device's name by its hardware address, or "" when the box has not seen
   it. */
export const deviceName = (rows, mac) => rows.find((d) => d.mac === DW.normalizeMac(mac))?.name || "";
